// src/pages/ForgotPasswordPage.js
import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Mail, Lock, KeyRound, ArrowLeft, Eye, EyeOff, CheckCircle2, ShieldCheck } from "lucide-react";

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { forgotPassword, verifyResetCode, resetPassword } = useAuth();

  // Query parameter support: /reset-password?email=...&token=...
  const initialEmail = searchParams.get("email") || localStorage.getItem("briq_remembered_email") || "";
  const initialToken = searchParams.get("token") || "";

  // Step 1: Request Code | Step 2: Enter Code & New Password
  const [step, setStep] = useState(initialToken ? 2 : 1);
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState("");
  const [resetToken, setResetToken] = useState(initialToken);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [devCode, setDevCode] = useState(null);

  // Enforce pure light mode on auth screens
  useEffect(() => {
    document.documentElement.classList.remove("dark");
  }, []);

  // STEP 1: Request Reset Code via API
  const handleRequestCode = async (e) => {
    e.preventDefault();
    if (!email || !email.trim()) {
      toast.error("Please enter your email address.");
      return;
    }

    setLoading(true);
    try {
      const res = await forgotPassword(email.trim());
      if (res.success) {
        toast.success(res.message || "Verification code sent to your email!");
        if (res.resetToken) setResetToken(res.resetToken);
        if (res.devCode) {
          setDevCode(res.devCode);
          setCode(res.devCode); // Auto-populate for developer convenience
        }
        setStep(2);
      } else {
        toast.error(res.message || "Could not find an account with this email.");
      }
    } catch (err) {
      console.error("Forgot password request error:", err);
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Resend code handler
  const handleResendCode = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const res = await forgotPassword(email.trim());
      if (res.success) {
        toast.success("A new verification code has been dispatched!");
        if (res.resetToken) setResetToken(res.resetToken);
        if (res.devCode) {
          setDevCode(res.devCode);
          setCode(res.devCode);
        }
      } else {
        toast.error(res.message || "Failed to resend code.");
      }
    } catch (err) {
      toast.error("Failed to resend verification code.");
    } finally {
      setLoading(false);
    }
  };

  // STEP 2: Verify code and reset password
  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (!code || code.trim().length < 4) {
      toast.error("Please enter the verification code sent to your email.");
      return;
    }

    if (!newPassword || newPassword.length < 6) {
      toast.error("New password must be at least 6 characters long.");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match. Please verify.");
      return;
    }

    setLoading(true);
    try {
      const res = await resetPassword(email.trim(), code.trim(), resetToken, newPassword);
      if (res.success) {
        toast.success(res.message || "Password successfully reset! Logging you in...");
        setTimeout(() => {
          navigate(res.homeRoute || "/admin/dashboard");
        }, 800);
      } else {
        toast.error(res.message || "Failed to reset password. Please check your code.");
      }
    } catch (err) {
      console.error("Reset password error:", err);
      toast.error("Failed to reset password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-auth-page="true"
      className="min-h-screen flex items-center justify-center bg-[#F7F4EE] px-4 py-8 auth-light-only"
    >
      <div className="bg-white shadow-xl rounded-2xl p-8 w-full max-w-md border border-[#E1E7EE]">
        {/* Brand Header */}
        <div className="text-center mb-6">
          <img
            src="/assets/branding/briqerp-logo-light.svg"
            alt="BriqERP"
            className="h-10 sm:h-12 w-auto mx-auto object-contain mb-3"
          />
          <h2 className="text-xl font-bold text-[#1B2A41]">
            {step === 1 ? "Forgot Password?" : "Set New Password"}
          </h2>
          <p className="text-xs text-[#6B7A90] font-medium mt-1 leading-relaxed">
            {step === 1
              ? "Enter your registered email address and we'll send a 6-digit verification code to reset your password."
              : `Enter the verification code sent to ${email} along with your new password.`}
          </p>
        </div>

        {/* STEP 1: Request Code Form */}
        {step === 1 && (
          <form onSubmit={handleRequestCode} className="space-y-4" autoComplete="off">
            <div>
              <label className="block text-xs font-bold text-[#1B2A41] uppercase tracking-wide mb-1">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                  <Mail className="w-4 h-4" />
                </div>
                <input
                  type="email"
                  name="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@company.com"
                  className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#E8612C] focus:border-transparent transition"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full bg-[#E8612C] text-white py-2.5 rounded-lg hover:bg-[#D44E1C] transition-all font-semibold shadow-md disabled:opacity-50 cursor-pointer ${
                loading ? "cursor-not-allowed opacity-75" : ""
              }`}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Sending Code...
                </span>
              ) : (
                "Send Verification Code"
              )}
            </button>

            <div className="pt-2 text-center">
              <Link
                to="/login"
                className="inline-flex items-center gap-1.5 text-xs text-[#6B7A90] hover:text-[#1B2A41] font-semibold transition"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Back to Sign In
              </Link>
            </div>
          </form>
        )}

        {/* STEP 2: Enter Verification Code & New Password */}
        {step === 2 && (
          <form onSubmit={handleResetPassword} className="space-y-4" autoComplete="off">
            {/* Dev Mode Notification if active */}
            {devCode && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800 flex items-start gap-2">
                <ShieldCheck className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <span className="font-bold">Local / Dev Code:</span>{" "}
                  <code className="bg-amber-100 px-1.5 py-0.5 rounded font-mono font-bold tracking-wider">
                    {devCode}
                  </code>
                  <p className="text-[11px] text-amber-700 mt-0.5">
                    Code has been pre-filled for your convenience.
                  </p>
                </div>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-bold text-[#1B2A41] uppercase tracking-wide">
                  6-Digit Verification Code
                </label>
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={loading}
                  className="text-xs text-[#E8612C] hover:underline font-semibold cursor-pointer disabled:opacity-50"
                >
                  Resend Code
                </button>
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                  <KeyRound className="w-4 h-4" />
                </div>
                <input
                  type="text"
                  maxLength={6}
                  required
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="123456"
                  className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm text-[#111827] font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-[#E8612C] focus:border-transparent transition"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-[#1B2A41] uppercase tracking-wide mb-1">
                New Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                  <Lock className="w-4 h-4" />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  minLength={6}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  className="w-full pl-9 pr-10 py-2.5 border border-gray-300 rounded-lg text-sm text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#E8612C] focus:border-transparent transition"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-[#1B2A41] uppercase tracking-wide mb-1">
                Confirm New Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                  <Lock className="w-4 h-4" />
                </div>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  required
                  minLength={6}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-type new password"
                  className="w-full pl-9 pr-10 py-2.5 border border-gray-300 rounded-lg text-sm text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#E8612C] focus:border-transparent transition"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full bg-[#E8612C] text-white py-2.5 rounded-lg hover:bg-[#D44E1C] transition-all font-semibold shadow-md disabled:opacity-50 cursor-pointer ${
                loading ? "cursor-not-allowed opacity-75" : ""
              }`}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Updating Password...
                </span>
              ) : (
                "Reset Password & Sign In"
              )}
            </button>

            <div className="flex items-center justify-between pt-2">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="text-xs text-[#6B7A90] hover:text-[#1B2A41] font-semibold transition cursor-pointer"
              >
                ← Change Email
              </button>
              <Link
                to="/login"
                className="text-xs text-[#E8612C] hover:underline font-semibold"
              >
                Back to Sign In
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
